import { pinFoot } from '../pin-foot'
import { addressLine } from '../address-words'
import { api, ApiError } from '../api'
import { sheet } from '../day-close'
import { findCustomers } from '../customer-find'
import { invoicesOn } from '../business-settings'
import { cannotSend, monthName, openInvoice, openInvoiceSetUp, openNewInvoice, readyToInvoice, stateChip, STATE_WORDS, type ReadyVisit } from '../invoices'
import { invoiceRecordsCsv, recordState, taxYears, type DateRange, type RecordRow } from '../invoice-records'
import { reducedMotion } from '../motion'
import { postcodeWords, roundConfig, roundDay, screenLocale } from '../round-config'
import { cursorOnOpen } from '../sheet-keyboard'
import { clear, el, money, toast } from '../ui'
import { standingWords } from '../visit-words'
import type { Customer } from '../types'

// More > Invoices (Liam, 22 Oct 2026: "an Invoice Generator in Roundly under More", then "a list of past invoices
// generated, useful for taxes or looking back"; docs/plans/invoice-maker/PLAN.md, Astra two rounds, Sonnet and GPT-6.1 on
// the plan). The owner's only. Make an invoice from jobs done (the existing check-and-send step), every invoice made,
// newest first, and a download of the records. Collection stays in Owed: this page counts nothing Owed counts.

// At most 12 jobs ticked on one invoice: the bank tick-off pays at most 12 visits in one go (GPT-6.1:
// functions/api/bank-refs/apply.ts), so a bank payment of the invoice always ticks every job off.
export const MAX_JOBS = 12

let rows: RecordRow[] = []
let more = false
let state: 'loading' | 'ready' | 'failed' = 'loading'
let landed = new Set<number>()
let gen = 0

// A chevron drawn, not typed: a "›" sits low beside the words (Liam, 22 Oct 2026: "arrows arent vertically aligned").
function chevron(): SVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  svg.setAttribute('class', 'invoices-chevron'); svg.setAttribute('viewBox', '0 0 24 24'); svg.setAttribute('aria-hidden', 'true')
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
  path.setAttribute('d', 'm9 6 6 6-6 6')
  svg.append(path)
  return svg
}
const signed = (p: number): string => (p < 0 ? `-${money(-p)}` : money(p))
const dateWords = (iso: string): string => new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString(screenLocale(), { day: 'numeric', month: 'short' })
const monthHeading = (iso: string): string => new Date(`${iso.slice(0, 7)}-15T12:00:00`).toLocaleDateString(screenLocale(), { month: 'long', year: 'numeric' })

export function mountInvoices(): void {
  const root = document.querySelector<HTMLElement>('#screen')!
  // From Extra features' "Make an invoice" (#/invoices?new=1, design B, 25 Oct 2026): the New invoice pop-up opens once.
  const wantsNew = /[?&]new=1\b/.test(window.location.hash)
  if (wantsNew) window.history?.replaceState?.(window.history.state, '', '#/invoices')
  const page = el('div', { class: 'invoices-page' })
  page.append(el('div', { class: 'page-heading' }, el('div', {}, el('h2', {}, 'Invoices'), el('p', { class: 'muted' }, "Make an invoice, and see every one you've made."))))
  const top = el('div', { class: 'invoices-top' })
  const stop = cannotSend()
  if (stop) top.append(el('p', { class: 'invoice-note invoice-paused' }, stop))
  else if (!invoicesOn()) {
    // Off, or never set up: say so before the jump, and let the list below still show what was made before.
    const set = el('button', { type: 'button', class: 'btn-primary invoices-new' }, 'Set up invoices')
    set.addEventListener('click', () => openInvoiceSetUp())
    top.append(set, el('p', { class: 'muted' }, 'Invoices are switched off. Set them up in Extra features to make new ones.'))
  } else {
    const make = el('button', { type: 'button', class: 'btn-primary invoices-new' }, '+ New invoice')
    make.addEventListener('click', () => openNewInvoiceSheet())
    top.append(make, el('a', { href: '#/owed', class: 'invoices-owed-link' }, el('span', {}, 'Sent and not paid yet? They’re in Owed'), chevron()))
    if (wantsNew) openNewInvoiceSheet()
  }
  page.append(top)
  const head = el('div', { class: 'invoices-list-head' }, el('h3', {}, 'Every invoice'))
  const download = el('button', { type: 'button', class: 'link-button invoices-download' }, 'Download')
  download.addEventListener('click', () => openDownload())
  head.append(download)
  page.append(head, el('div', { class: 'invoices-all', 'aria-live': 'polite' }))
  root.append(page)
  rows = []; more = false; state = 'loading'; landed = new Set()
  draw()
  void load()
}

// A change anywhere (paid in Owed, a send, a credit note): the pages already read, read again; nothing else moves.
export function invoicesOnDataChanged(): void {
  if (!document.querySelector('.invoices-page')) return
  void load(rows.length > 100 ? rows.length : 100)
}

async function load(count = 100, before?: number): Promise<void> {
  const mine = ++gen
  try {
    const got: RecordRow[] = []
    let next = before
    let moreNow = false
    do {
      const r = await api<{ invoices: RecordRow[]; more: boolean }>(`/api/invoices?all=1${next ? `&before_seq=${next}` : ''}`)
      if (mine !== gen) return
      got.push(...r.invoices)
      moreNow = r.more
      next = Number((r.invoices.at(-1) as { seq?: number } | undefined)?.seq ?? 0) || undefined
    } while (moreNow && got.length < count && next)
    const known = new Set(rows.map((r) => r.id))
    // A document made since the last read lands at the top with a lemon tint, once (ANIMATION-STANDARD: confirm a save).
    if (rows.length && !before) for (const r of got) if (!known.has(r.id)) landed.add(r.id)
    rows = before ? [...rows, ...got] : got
    more = moreNow
    state = 'ready'
  } catch {
    if (mine !== gen) return
    // Never an empty list for want of signal: "no invoices" would read as none at tax time (Sonnet).
    if (!rows.length) state = 'failed'
    else toast("Couldn't read your invoices. Check your signal and try again.")
  }
  draw()
}

function draw(): void {
  const host = document.querySelector<HTMLElement>('.invoices-all')
  if (!host) return
  clear(host)
  if (state === 'loading' && !rows.length) { host.append(el('p', { class: 'muted' }, 'Getting your invoices…')); return }
  if (state === 'failed') {
    const again = el('button', { type: 'button' }, 'Try again')
    again.addEventListener('click', () => { state = 'loading'; draw(); void load() })
    host.append(el('p', {}, "Couldn't read your invoices. No signal, or Roundly didn't answer. Nothing has changed."), again)
    return
  }
  if (!rows.length) { host.append(el('p', { class: 'muted invoices-empty' }, 'No invoices yet. Every one you make shows here.')); return }
  // Grouped by the month each was made, newest first (by number: the order they were made).
  let month = ''
  let group: HTMLElement | null = null
  for (const r of rows) {
    const m = r.issued_on.slice(0, 7)
    if (m !== month) {
      month = m
      host.append(el('h4', { class: 'invoices-month-head' }, monthHeading(r.issued_on)))
      group = el('div', { class: 'invoices-group' })
      host.append(group)
    }
    const who = r.to?.for_customer || r.to?.name || r.customer_name || 'Customer'
    const amount = signed(r.kind === 'credit' ? r.total_pence : r.subtotal_pence)
    const what = r.kind === 'credit' ? `Credit note${r.credit_for_number ? ` for ${r.credit_for_number}` : ''}` : r.kind === 'month' && r.period ? `${monthName(r.period)}’s` : ''
    const row = el('button', { type: 'button', class: `invoices-row${landed.has(r.id) ? ' invoices-row-new' : ''}` },
      el('span', { class: 'invoices-row-text' }, el('strong', {}, who), el('small', {}, [r.number, dateWords(r.issued_on), amount, what].filter(Boolean).join(' · '))),
      stateChip(recordState(r)))
    row.setAttribute('aria-label', `${who}, ${r.number}, ${amount}, ${STATE_WORDS[recordState(r)]}`)
    row.addEventListener('click', () => openInvoice(r.id, { done: () => void load(Math.max(100, rows.length)) }))
    group!.append(row)
  }
  landed.clear()
  if (more) {
    const older = el('button', { type: 'button', class: 'invoices-older' }, 'Show older')
    older.addEventListener('click', () => {
      older.disabled = true
      older.textContent = 'Getting older invoices…'
      void load(100, Number((rows.at(-1) as { seq?: number } | undefined)?.seq ?? 0) || undefined)
    })
    host.append(older)
  }
}

// ---- New invoice: who, then which jobs -------------------------------------------------------------------------------
function openNewInvoiceSheet(): void {
  const s = sheet('New invoice')
  s.head.append(el('h2', {}, 'New invoice'))
  const body = s.body
  body.classList.add('invoice-maker')
  body.append(el('p', { class: 'muted' }, 'Getting your customers…'))
  void api<{ customers: Customer[] }>('/api/customers').then(({ customers }) => {
    clear(body)
    const search = el('input', { type: 'search', class: 'invoice-maker-search', placeholder: postcodeWords('Name, address or postcode'), 'aria-label': 'Who is it for?' }) as HTMLInputElement
    const list = el('div', { class: 'customer-picker' })
    const drawList = (): void => {
      clear(list)
      const matches = findCustomers(customers, search.value)
      for (const c of matches.slice(0, 30)) {
        const pick = el('button', { type: 'button', class: 'customer-choice' }, el('strong', {}, c.name), el('span', {}, [addressLine(c.address, c.postcode, ' · '), standingWords(c)].filter(Boolean).join(' · ')))
        pick.addEventListener('click', () => chooseJobs(s, c))
        list.append(pick)
      }
      if (!matches.length) list.append(el('p', {}, postcodeWords('Nobody matches. Try another name or postcode.')))
      if (matches.length > 30) list.append(el('p', { class: 'muted' }, 'Type to narrow the list.'))
    }
    search.addEventListener('input', drawList)
    body.append(el('label', { class: 'invoice-maker-label' }, 'Who is it for?', search), list)
    drawList()
    cursorOnOpen(search)
  }).catch(() => {
    clear(body)
    body.append(el('p', {}, 'Invoices need signal. Try again when you have signal.'))
  })
}

function chooseJobs(s: ReturnType<typeof sheet>, c: Customer): void {
  const body = s.body
  clear(body)
  body.append(el('p', { class: 'invoice-maker-who' }, el('strong', {}, c.name), addressLine(c.address, c.postcode) ? ` · ${addressLine(c.address, c.postcode)}` : ''))
  const back = el('button', { type: 'button', class: 'link-button' }, 'Someone else')
  back.addEventListener('click', () => { s.close(); openNewInvoiceSheet() })
  body.append(back)
  const area = el('div', { class: 'invoice-maker-jobs' }, el('p', { class: 'muted' }, 'Getting their jobs…'))
  body.append(area)
  void readyToInvoice(c.id).then((ready) => {
    clear(area)
    const send = (ask: { cleanIds?: number[]; month?: string }): void => { s.close(); openNewInvoice(c.id, ask, { phone: c.phone ?? null, done: () => void load(Math.max(100, rows.length)) }) }
    if (!ready.visits.length) { area.append(el('p', { class: 'muted' }, 'Nothing to invoice: no done jobs with a price that are not on an invoice already.')); return }
    const jobs = (): void => drawJobs(area, ready.visits, (ids) => send({ cleanIds: ids }))
    // Once a month: their months, as their page offers them; picking jobs one by one is the exception, said so (Astra).
    if (ready.monthly && ready.months.length) {
      area.append(el('p', { class: 'muted' }, `${c.name.split(' ')[0]} is invoiced once a month.`))
      for (const m of ready.months.slice(0, 3)) {
        const b = el('button', { type: 'button', class: 'btn-primary invoice-maker-month' }, `Send ${monthName(m.period)}’s`)
        // Everything in it paid already: nothing to ask for (the jobs list says the same).
        b.disabled = m.over || m.to_pay_pence <= 0
        b.addEventListener('click', () => send({ month: m.period }))
        area.append(el('div', { class: 'invoices-month' }, el('span', {}, el('strong', {}, monthName(m.period)), el('br'),
          el('small', { class: 'muted' }, `${m.visits} job${m.visits === 1 ? '' : 's'} · ${money(m.to_pay_pence)}${m.over ? ' · more than 31, pick them below' : ''}`)), b))
      }
      const pick = el('button', { type: 'button', class: 'link-button' }, 'Or pick jobs one by one')
      pick.addEventListener('click', () => { clear(area); jobs() })
      area.append(pick)
      return
    }
    jobs()
  }).catch((e) => {
    clear(area)
    area.append(el('p', {}, e instanceof ApiError && e.status === 404 ? 'That customer is no longer on your round.' : 'Invoices need signal. Try again when you have signal.'))
  })
}

// The jobs, newest first. Not paid yet is ticked; Paid, covered by credit, no payment recorded and ones on a cancelled
// invoice before are left for a tick. The total is the invoice's own: each job less what is already paid or set against
// it (readyFor's less_pence), so the check step shows the same figure.
function drawJobs(area: HTMLElement, visits: ReadyVisit[], go: (ids: number[]) => void): void {
  const covered = (v: ReadyVisit): boolean => v.less_pence >= v.amount_pence
  const ticked = new Set(visits.filter((v) => v.payment_status === 'unpaid' && !covered(v) && !v.was_on).slice(0, MAX_JOBS).map((v) => v.id))
  area.append(el('h3', { class: 'invoice-maker-heading' }, 'Jobs done, not on an invoice yet'))
  const list = el('div', { class: 'invoice-maker-list' })
  const total = el('strong', { class: 'invoice-maker-total-figure' })
  const why = el('p', { class: 'muted invoice-maker-why', role: 'status' })
  const check = el('button', { type: 'button', class: 'btn-primary invoice-maker-check' }, 'Check it')
  let shown = 0
  const refresh = (): void => {
    const picked = visits.filter((v) => ticked.has(v.id))
    const sum = picked.reduce((s, v) => s + v.amount_pence - v.less_pence, 0)
    countTo(total, shown, sum)
    shown = sum
    const reason = !picked.length ? 'Tick the jobs to put on it.'
      : sum <= 0 ? 'Everything ticked is paid already, so there is nothing to ask for.'
      : picked.length >= MAX_JOBS ? `${MAX_JOBS} jobs is the most on one invoice, so a bank payment can tick them all off.` : ''
    why.textContent = reason
    check.disabled = !picked.length || sum <= 0
    for (const box of Array.from(list.querySelectorAll<HTMLInputElement>('input[type=checkbox]'))) box.disabled = !box.checked && picked.length >= MAX_JOBS
  }
  for (const v of visits) {
    const status = v.was_on ? `Was on ${v.was_on}, cancelled` : covered(v) && v.payment_status !== 'paid' ? 'Covered by credit' : v.payment_status === 'paid' ? 'Paid' : v.payment_status === 'unpaid' ? 'Not paid yet' : 'No payment recorded'
    const box = el('input', { type: 'checkbox', class: 'invoice-maker-tick' }) as HTMLInputElement
    box.checked = ticked.has(v.id)
    const row = el('label', { class: `invoice-maker-row${box.checked ? ' invoice-maker-row-on' : ''}` }, box,
      el('span', { class: 'invoice-maker-row-text' }, el('span', {}, v.label), el('small', { class: `invoice-maker-status invoice-maker-status-${v.payment_status}` }, status)),
      el('strong', {}, money(v.amount_pence)))
    box.addEventListener('change', () => {
      // No payment recorded: asked first, as the visit's own Invoice button asks (the invoice turns it into Not paid yet).
      if (box.checked && v.payment_status === 'unknown') {
        box.checked = false
        askUnknown(row, v, () => {
          // Ticked up to the most while the question was open: it stays off (Sonnet).
          if (ticked.size >= MAX_JOBS) { toast(`${MAX_JOBS} jobs is the most on one invoice.`); return }
          box.checked = true; ticked.add(v.id); row.classList.add('invoice-maker-row-on'); refresh()
        })
        return
      }
      if (box.checked) ticked.add(v.id); else ticked.delete(v.id)
      row.classList.toggle('invoice-maker-row-on', box.checked)
      refresh()
    })
    list.append(row)
  }
  check.addEventListener('click', () => { if (!check.disabled) go(visits.filter((v) => ticked.has(v.id)).map((v) => v.id)) })
  area.append(list, el('div', { class: 'invoice-maker-total' }, el('span', {}, 'To pay'), total), why, pinFoot(check))
  refresh()
}

function askUnknown(row: HTMLElement, v: ReadyVisit, yes: () => void): void {
  row.parentElement?.querySelector('.invoice-maker-ask')?.remove()
  const ask = el('div', { class: 'invoice-maker-ask', role: 'group', 'aria-label': 'Already paid?' },
    el('p', {}, `No payment is recorded for ${v.label.split(', ').slice(-1)[0]}. An invoice asks them for the money.`))
  const notPaid = el('button', { type: 'button', class: 'btn-primary' }, 'Not paid yet: put it on')
  notPaid.addEventListener('click', () => { ask.remove(); yes() })
  const paid = el('button', { type: 'button' }, 'They have paid: leave it off')
  paid.addEventListener('click', () => { ask.remove(); toast('Left off. Mark it paid on the visit or in Owed.') })
  ask.append(el('div', { class: 'invoice-maker-ask-actions' }, notPaid, paid))
  row.after(ask)
}

// The total moves to its new figure in 180ms (ANIMATION-STANDARD: a count update where the action happens); at once with
// reduced motion.
function countTo(node: HTMLElement, from: number, to: number): void {
  if (reducedMotion() || from === to || typeof requestAnimationFrame !== 'function') { node.textContent = money(to); return }
  const start = performance.now()
  const step = (now: number): void => {
    const t = Math.min(1, (now - start) / 180)
    const eased = 1 - (1 - t) * (1 - t)
    node.textContent = money(Math.round(from + (to - from) * eased))
    if (t < 1) requestAnimationFrame(step)
  }
  requestAnimationFrame(step)
  node.classList.remove('invoice-maker-bump'); void node.offsetWidth; node.classList.add('invoice-maker-bump')
}

// ---- Download the records ---------------------------------------------------------------------------------------------
function openDownload(): void {
  const s = sheet('Download invoice records')
  s.head.append(el('h2', {}, 'Download invoice records'))
  const cfg = roundConfig()
  const years = taxYears(roundDay(), cfg.country)
  const uk = cfg.country === 'GB'
  let range: DateRange = years.thisYear
  const picks = el('div', { class: 'invoices-download-picks', role: 'group', 'aria-label': 'Which dates' })
  const fromBox = el('input', { type: 'date', 'aria-label': 'From' }) as HTMLInputElement
  const toBox = el('input', { type: 'date', 'aria-label': 'To' }) as HTMLInputElement
  const own = el('div', { class: 'invoices-download-own', hidden: '' }, el('label', {}, 'From', fromBox), el('label', {}, 'To', toBox))
  const saying = el('p', { class: 'invoices-download-range' })
  const say = (): void => { saying.textContent = range.label }
  const choices: [string, () => void][] = [
    [uk ? 'This tax year' : 'This year', () => { range = years.thisYear; own.hidden = true; say() }],
    [uk ? 'Last tax year' : 'Last year', () => { range = years.lastYear; own.hidden = true; say() }],
    ['Choose dates', () => { own.hidden = false; fromBox.value = range.from; toBox.value = range.to; range = { ...range, label: 'Your dates' }; say() }],
  ]
  choices.forEach(([label, act], i) => {
    const b = el('button', { type: 'button', 'aria-pressed': String(i === 0) }, label)
    b.addEventListener('click', () => { for (const o of Array.from(picks.children)) o.setAttribute('aria-pressed', String(o === b)); act() })
    picks.append(b)
  })
  const ownRange = (): void => { if (fromBox.value && toBox.value) range = { from: fromBox.value, to: toBox.value, label: 'Your dates' } }
  fromBox.addEventListener('change', ownRange); toBox.addEventListener('change', ownRange)
  const go = el('button', { type: 'button', class: 'btn-primary invoices-download-go' }, 'Download (spreadsheet)')
  go.addEventListener('click', () => {
    // Chosen dates are read from the boxes as they are now, and the request keeps its own copy (GPT-6.1: a cleared box
    // sent the dates before it, and a change mid-download renamed the file).
    if (!own.hidden) {
      if (!fromBox.value || !toBox.value) { toast('Pick both dates first.'); return }
      range = { from: fromBox.value, to: toBox.value, label: 'Your dates' }
    }
    const asked = { ...range }
    if (asked.from > asked.to) { toast('The first date has to come before the second.'); return }
    go.disabled = true
    go.textContent = 'Getting your invoices…'
    void api<{ invoices: RecordRow[]; more: boolean }>(`/api/invoices?all=1&from=${asked.from}&to=${asked.to}`).then((r) => {
      const csv = invoiceRecordsCsv(r.invoices, { zone: cfg.timeZone, currency: cfg.currency, vat: uk, today: roundDay() })
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
      const a = el('a', { href: url, download: `roundly-invoices-${asked.from}-to-${asked.to}.csv` })
      document.body.append(a); a.click(); a.remove()
      window.setTimeout(() => URL.revokeObjectURL(url), 10_000)
      toast(r.more ? 'The first 5000 invoices downloaded. Choose fewer dates for the rest.' : `${r.invoices.length} invoice record${r.invoices.length === 1 ? '' : 's'} downloaded`)
      s.close()
    }).catch(() => {
      toast("Couldn't download. Check your signal and try again.")
    }).finally(() => { go.disabled = false; go.textContent = 'Download (spreadsheet)' })
  })
  s.body.append(picks, own, saying,
    el('div', { class: 'invoices-download-note' },
      el('p', {}, 'One line for each invoice and credit note you made, including ones not sent yet and ones cancelled: number, date, customer, what it was for, the price, anything already paid, VAT, and where it stands today.'),
      el('p', {}, el('strong', {}, 'Invoices made, not money received.'))),
    go)
  say()
}

